/*
 * Countdown to a fixed date.
 *
 * Shared by the announcement bar, the launch strip in the buy box and the
 * launch section. It counts to a date typed into the theme, never to "now plus
 * N hours" — a timer that restarts per visitor is a prohibited practice, and it
 * is also what stops a launch price from ever becoming a real one.
 *
 * Markup contract, all optional except the root and the timer:
 *
 *   [data-wake-countdown]           root, carries data-deadline
 *     [data-wake-countdown-text]    swapped for data-expired-text when it ends
 *     [data-wake-countdown-timer]   hidden until the first tick
 *       [data-wake-countdown-clock]   one line: "12d 4t 30m 08s"
 *       [data-wake-countdown-days]    or a box per unit, zero padded
 *       [data-wake-countdown-hours]
 *       [data-wake-countdown-minutes]
 *       [data-wake-countdown-seconds]
 */

(function () {
  function pad(value) {
    return String(value).padStart(2, '0');
  }

  function setup(root) {
    if (root.dataset.wakeBound) return;
    root.dataset.wakeBound = 'true';

    const raw = root.dataset.deadline;
    if (!raw) return;

    /* Parsed as local time on purpose: somebody typing 30 September means the
       end of that day where they are, not UTC. */
    const deadline = new Date(raw).getTime();
    if (Number.isNaN(deadline)) return;

    const timer = root.querySelector('[data-wake-countdown-timer]');
    if (!timer) return;

    const text = root.querySelector('[data-wake-countdown-text]');
    const clock = root.querySelector('[data-wake-countdown-clock]');
    const units = {
      days: root.querySelector('[data-wake-countdown-days]'),
      hours: root.querySelector('[data-wake-countdown-hours]'),
      minutes: root.querySelector('[data-wake-countdown-minutes]'),
      seconds: root.querySelector('[data-wake-countdown-seconds]'),
    };
    const hasUnits = Boolean(units.days || units.hours || units.minutes || units.seconds);

    /* Nothing to write into, so nothing to run. */
    if (!clock && !hasUnits) return;

    let interval = null;

    function expire() {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
      timer.hidden = true;

      const after = root.dataset.expiredText;
      if (after && text) {
        text.textContent = after;
      } else {
        /* Nothing left to say, so it removes itself rather than advertising an
           offer that has ended. */
        root.hidden = true;
      }
    }

    function tick() {
      const left = deadline - Date.now();
      if (left <= 0) {
        expire();
        return;
      }

      const days = Math.floor(left / 86400000);
      const hours = Math.floor((left % 86400000) / 3600000);
      const minutes = Math.floor((left % 3600000) / 60000);
      const seconds = Math.floor((left % 60000) / 1000);

      if (units.days) units.days.textContent = pad(days);
      if (units.hours) units.hours.textContent = pad(hours);
      if (units.minutes) units.minutes.textContent = pad(minutes);
      if (units.seconds) units.seconds.textContent = pad(seconds);

      if (clock) {
        /* Seconds always run: a clock that visibly moves is the point of
           putting one here at all. Days drop off once there are none. */
        const parts = [];
        if (days > 0) parts.push(days + 'd');
        parts.push(hours + 't');
        parts.push(minutes + 'm');
        parts.push(seconds + 's');
        clock.textContent = parts.join(' ');
      }

      timer.hidden = false;
    }

    tick();
    interval = setInterval(tick, 1000);
  }

  function init(scope) {
    (scope || document).querySelectorAll('[data-wake-countdown]').forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  /* Exposed for the test harness; harmless in a browser. */
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { setup: setup, pad: pad };
  }
})();
